/**
 * Contact form — validate fields, then submit via REST (AJAX).
 */

/**
 * Render the explicit Turnstile widget once the API script is available.
 *
 * @param {HTMLFormElement} form
 * @return {void}
 */
function mountTurnstile( form ) {
	const slot = form.querySelector( '.hoplog-turnstile' );
	if ( ! slot || slot.dataset.widgetId || ! window.turnstile ) {
		return;
	}

	const sitekey = slot.dataset.sitekey;
	if ( ! sitekey ) {
		return;
	}

	const widgetId = window.turnstile.render( slot, {
		sitekey,
		action: 'contact',
	} );
	slot.dataset.widgetId = String( widgetId );
}

/**
 * @param {HTMLFormElement} form
 * @return {string}
 */
function readTurnstileToken( form ) {
	const slot = form.querySelector( '.hoplog-turnstile' );
	const widgetId = slot && slot.dataset.widgetId;
	if ( window.turnstile && widgetId && typeof window.turnstile.getResponse === 'function' ) {
		return window.turnstile.getResponse( widgetId ) || '';
	}

	const input = form.querySelector( '[name="cf-turnstile-response"]' );
	return input ? input.value : '';
}

/**
 * @param {HTMLFormElement} form
 * @return {void}
 */
function resetTurnstileWidget( form ) {
	const slot = form.querySelector( '.hoplog-turnstile' );
	const widgetId = slot && slot.dataset.widgetId;
	if ( window.turnstile && widgetId && typeof window.turnstile.reset === 'function' ) {
		window.turnstile.reset( widgetId );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.hoplog-contact-form__form' ).forEach( ( form ) => {
		const started = Date.now();
		const waitForTurnstile = window.setInterval( () => {
			if ( window.turnstile || Date.now() - started > 10000 ) {
				window.clearInterval( waitForTurnstile );
				mountTurnstile( form );
			}
		}, 50 );
		const fields = () =>
			form.querySelectorAll( 'input, select, textarea' );

		const clearInvalid = ( field ) => {
			field.classList.remove( 'is-invalid' );
			field.removeAttribute( 'aria-invalid' );
		};

		const markInvalid = ( field ) => {
			field.classList.add( 'is-invalid' );
			field.setAttribute( 'aria-invalid', 'true' );
		};

		const statusEl = form.querySelector( '.hoplog-contact-form__status' );
		const submitBtn = form.querySelector( 'button[type="submit"]' );
		const defaultLabel = submitBtn ? submitBtn.textContent : '';

		const showStatus = ( type, message ) => {
			if ( ! statusEl ) {
				return;
			}

			const alertClass =
				type === 'success' ? 'alert alert-success' : 'alert alert-error';

			statusEl.innerHTML = `<div class="${ alertClass }" tabindex="-1"><span></span></div>`;
			const span = statusEl.querySelector( 'span' );
			if ( span ) {
				span.textContent = message;
			}

			const alert = statusEl.querySelector( '.alert' );
			if ( alert && typeof alert.focus === 'function' ) {
				alert.focus();
			}
		};

		const clearStatus = () => {
			if ( statusEl ) {
				statusEl.innerHTML = '';
			}
		};

		fields().forEach( ( field ) => {
			const syncValidity = () => {
				if ( field.checkValidity() ) {
					clearInvalid( field );
				}
			};

			field.addEventListener( 'input', syncValidity );
			field.addEventListener( 'change', syncValidity );
		} );

		form.addEventListener( 'submit', async ( event ) => {
			if ( ! form.checkValidity() ) {
				event.preventDefault();

				let firstInvalid = null;

				fields().forEach( ( field ) => {
					clearInvalid( field );
					if ( ! field.checkValidity() ) {
						markInvalid( field );
						if ( ! firstInvalid ) {
							firstInvalid = field;
						}
					}
				} );

				if ( firstInvalid ) {
					firstInvalid.focus();
				}
				return;
			}

			// No fetch → allow native POST (PHP fallback on template_redirect).
			if ( typeof fetch !== 'function' ) {
				return;
			}

			event.preventDefault();

			const restUrl = form.dataset.restUrl;
			const nonceInput = form.querySelector( '[name="hoplog_contact_nonce"]' );
			const fallbackNonce = nonceInput ? nonceInput.value : '';
			const nonceUrl = restUrl
				? restUrl.replace( /\/contact\/?$/, '/contact-nonce' )
				: '';

			const turnstileToken = readTurnstileToken( form );
			if ( form.querySelector( '.hoplog-turnstile' ) && ! turnstileToken ) {
				showStatus(
					'error',
					'Please complete the security check and try again.'
				);
				return;
			}

			clearStatus();

			if ( submitBtn ) {
				submitBtn.disabled = true;
				submitBtn.textContent = 'Sending…';
			}

			try {
				let formNonce = fallbackNonce;
				if ( nonceUrl ) {
					try {
						const nonceResponse = await fetch( nonceUrl, {
							method: 'GET',
							credentials: 'same-origin',
							cache: 'no-store',
						} );
						if ( nonceResponse.ok ) {
							const nonceData = await nonceResponse.json();
							if ( nonceData && typeof nonceData.nonce === 'string' && nonceData.nonce ) {
								formNonce = nonceData.nonce;
								if ( nonceInput ) {
									nonceInput.value = formNonce;
								}
							}
						}
					} catch ( e ) {
						// Fall through to the hidden-field nonce.
					}
				}

				if ( ! restUrl || ! formNonce ) {
					showStatus(
						'error',
						'Unable to send your message right now. Please try again later.'
					);
					return;
				}

				const payload = {
					first_name: form.querySelector( '[name="hoplog_first_name"]' )?.value || '',
					last_name: form.querySelector( '[name="hoplog_last_name"]' )?.value || '',
					email: form.querySelector( '[name="hoplog_email"]' )?.value || '',
					phone: form.querySelector( '[name="hoplog_phone"]' )?.value || '',
					inquiry: form.querySelector( '[name="hoplog_inquiry"]' )?.value || '',
					business_type:
						form.querySelector( '[name="hoplog_business_type"]:checked' )?.value || '',
					message: form.querySelector( '[name="hoplog_message"]' )?.value || '',
					website: form.querySelector( '[name="hoplog_website"]' )?.value || '',
					page_url: window.location.href,
					hoplog_contact_nonce: formNonce,
					cf_turnstile_response: turnstileToken,
				};

				const response = await fetch( restUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'same-origin',
					body: JSON.stringify( payload ),
				} );

				let data = null;
				try {
					data = await response.json();
				} catch ( e ) {
					data = null;
				}

				if ( ! response.ok || ! data || data.success !== true ) {
					const message =
						( data && ( data.message || data.data?.message ) ) ||
						'Unable to send your message right now. Please try again later.';
					throw new Error( message );
				}

				form.reset();
				fields().forEach( clearInvalid );
				showStatus(
					'success',
					data.message || 'Thank you — your message has been sent.'
				);
			} catch ( err ) {
				showStatus(
					'error',
					err && err.message
						? err.message
						: 'Unable to send your message right now. Please try again later.'
				);
			} finally {
				resetTurnstileWidget( form );
				if ( submitBtn ) {
					submitBtn.disabled = false;
					submitBtn.textContent = defaultLabel;
				}
			}
		} );
	} );
} );
